import { useState } from "react";
import type { FormEvent, JSX } from "react";

import { Icon } from "../ui";
import { useAnalytics } from "../../analytics/provider";
import magnisLogoRoundedUrl from "../../../../desktop/icon-rounded.png";

interface PrivateWorkspaceModalProps {
  readonly initialEmail?: string | null;
  readonly onClose: () => void;
}

export function PrivateWorkspaceModal({
  initialEmail,
  onClose,
}: PrivateWorkspaceModalProps): JSX.Element {
  const analytics = useAnalytics();
  const [email, setEmail] = useState(() => initialEmail?.trim().toLowerCase() ?? "");
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const normalizedEmail = email.trim().toLowerCase();
      const emailDomain = normalizedEmail.split("@")[1]?.trim();
      if (analytics.mode !== "off") {
        await analytics.capture(
          "private_cloud_interest.submitted",
          {
            email_submitted: true,
            ...(emailDomain ? { email_domain: emailDomain } : {}),
          },
          {
            source: "private_workspace_modal",
            internalProperties: {
              email: normalizedEmail,
            },
          },
        );
      }
      setSubmitted(true);
    } catch (submitError) {
      setError(
        submitError instanceof Error
          ? submitError.message
          : "Could not submit your email.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/55 px-4">
      <div className="relative w-full max-w-lg rounded-[32px] border border-edge bg-surface px-8 py-8 shadow-2xl">
        <div className="absolute right-5 top-5">
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-full border-none bg-transparent text-content-secondary transition-colors hover:bg-surface-hover hover:text-content"
            aria-label="Close Private Workspace dialog"
          >
            <Icon name="close" size={18} />
          </button>
        </div>

        <div className="flex justify-center">
          <img
            src={magnisLogoRoundedUrl}
            alt="Magnis"
            className="h-16 w-16 rounded-[20px] object-cover shadow-[0_10px_30px_rgba(0,0,0,0.28)]"
          />
        </div>

        <h2 className="mx-auto mt-6 max-w-md text-center text-3xl font-semibold tracking-[-0.03em] text-content">
          Connect to Private Cloud
        </h2>
        <p className="mx-auto mt-4 max-w-md text-center text-sm leading-6 text-content-secondary">
          This feature isn&apos;t ready yet. Leave your email if you&apos;re
          interested and we&apos;ll let you know when it opens.
        </p>

        <form onSubmit={(event) => { void handleSubmit(event); }} className="mt-8">
          <input
            type="email"
            value={email}
            onChange={(event) => { setEmail(event.target.value); }}
            className="w-full rounded-2xl border border-edge bg-surface-tertiary px-4 py-3 text-base text-content"
            placeholder="you@example.com"
            autoComplete="email"
            aria-label="Email"
            disabled={submitted}
          />

          {error ? (
            <p role="alert" className="mt-3 text-sm text-red-400">
              {error}
            </p>
          ) : null}
          {submitted ? (
            <p className="mt-3 text-sm text-[#8de1be]">
              You&apos;re on the list. We&apos;ll email you when it opens.
            </p>
          ) : null}

          <div className="mt-5 flex justify-end">
            <button
              type="submit"
              disabled={submitting || submitted || email.trim().length === 0}
              className="inline-flex items-center justify-center rounded-2xl border-none bg-[#ff6b3d] px-5 py-3 text-sm font-semibold text-white transition-opacity disabled:opacity-50"
            >
              {submitting ? "Submitting..." : "Notify me"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
